import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonPage,
    IonTitle,
    IonToolbar,
    IonIcon,
    IonSpinner,
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Typography,
    Box,
    Alert,
    TablePagination,
} from "@mui/material";
import axios from "axios"; // 1. Import Axios
import { getAppSettings, baseUrl } from "../../../common/utils"; // 2. Import baseUrl

/* ================= TYPES BASED ON YOUR JSON ================= */

// 1. Delivery Details
interface DeliveryMeta {
    id: number;
    delivery_id: string;
    po_id: string;
    vendor_id: string;
    delivery_date: string;
    inspection_status: string;
    created_at: string;
}

// 2. Purchase Order Details
interface POMeta {
    id: number;
    po_id: string;
    po_name: string;
    status: string;
    total_amount: string;
}

// 3. Item Details
interface DeliveryItem {
    id: number;
    delivery_id: string;
    product_id: string;
    quantity_declared: number;
    quantity_accepted: number;
}

// 4. Product Details
interface ProductMeta {
    id: number;
    product_id: string;
    name: string;
    description: string;
}

// 5. The Container Object (One row in the "data" array)
interface DeliveryDataPacket {
    delivery: DeliveryMeta[];
    purchase_order: POMeta[];
    items: DeliveryItem[];
    products: ProductMeta[];
}

// 6. The API Response Root
interface ApiResponse {
    total: number;
    data: DeliveryDataPacket[];
}

// 7. Flattened Row for the Table
interface DeliveryRow {
    uniqueKey: number; // For React Key
    deliveryId: string;
    poName: string; // Extra context
    status: string;
    date: string;
    itemCount: number; // Calculated from items.length
    hasItems: boolean; // Logic for button visibility
}

/* ================= COMPONENT ================= */

const DeliveryNoticeDashboard = () => {
    const { siteName } = getAppSettings();

    const [deliveries, setDeliveries] = useState<DeliveryRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // --- Pagination States ---
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    /* ================= FETCH DATA (AXIOS) ================= */

    useEffect(() => {
        fetchDeliveries();
    }, [page, rowsPerPage]);

    const fetchDeliveries = async () => {
        try {
            setLoading(true);
            setError(null);

            const offset = page * rowsPerPage;

            // 3. Use Axios with baseUrl()
            const response = await axios.get<ApiResponse>(
                `${baseUrl()}delivery-notice?limit=${rowsPerPage}&offset=${offset}`
            );

            // 4. Access data directly
            const json = response.data;

            // 1. Set Total Records
            setTotalCount(json.total || 0);

            // 2. Map Data
            const mappedRows: DeliveryRow[] = (json.data || []).map((packet) => {
                // Safely access the first element of arrays since structure is [ { ... } ]
                const deliveryInfo = packet.delivery[0];
                const poInfo = packet.purchase_order[0];
                const itemsList = packet.items || [];

                return {
                    uniqueKey: deliveryInfo.id,
                    deliveryId: deliveryInfo.delivery_id,
                    poName: poInfo ? poInfo.po_name : "Unknown PO",
                    status: deliveryInfo.inspection_status,
                    date: deliveryInfo.delivery_date,
                    itemCount: itemsList.length, // Calculating the count
                    hasItems: itemsList.length > 0, // Boolean for button logic
                };
            });

            setDeliveries(mappedRows);
        } catch (err: any) {
            console.error(err);
            // 5. Handle Axios errors safely
            const errorMessage = err.response?.data?.message || err.message || "Failed to fetch delivery notices";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    /* ================= HELPERS ================= */

    const getStatusConfig = (status: string) => {
        // Normalize for comparison
        const s = status.toLowerCase();

        if (s.includes("pending")) {
            return { color: "warning", label: "Pending Inspection" };
        }
        if (s.includes("inspected") || s.includes("completed")) {
            return { color: "success", label: "Inspected" };
        }
        if (s.includes("discrepancy") || s.includes("rejected")) {
            return { color: "error", label: "Discrepancy Found" };
        }
        return { color: "default", label: status };
    };

    // --- Pagination Handlers ---
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleActionClick = (id: string) => {
        console.log("View details for:", id);
        // Implement modal or navigation logic here
    };

    /* ================= RENDER ================= */

    return (
        <IonPage className="pushDown">
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonMenuButton />
                    </IonButtons>
                    <IonTitle>{siteName || "Delivery Notices"}</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="ion-padding">
                <Box sx={{ p: 2 }}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                        Delivery Notice
                    </Typography>

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Paper
                        sx={{
                            borderRadius: 3,
                            overflow: "hidden",
                            backgroundColor: "rgba(15,23,42,0.85)", // Dark theme base
                            backdropFilter: "blur(6px)",
                            boxShadow: "0 12px 30px rgba(0,0,0,0.45)",
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {loading && deliveries.length === 0 ? (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                height="300px"
                            >
                                <IonSpinner name="crescent" />
                            </Box>
                        ) : (
                            <>
                                <TableContainer>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                {["Delivery ID", "Status", "Date", "Item Number"].map(
                                                    (header) => (
                                                        <TableCell
                                                            key={header}
                                                            sx={{
                                                                backgroundColor: "rgba(2,6,23,0.95)",
                                                                color: "#cbd5f5",
                                                                fontWeight: 600,
                                                                fontSize: "0.75rem",
                                                                letterSpacing: "0.08em",
                                                                textTransform: "uppercase",
                                                            }}
                                                            align={
                                                                header === "Item Number" || header === "Action"
                                                                    ? "center"
                                                                    : "left"
                                                            }
                                                        >
                                                            {header}
                                                        </TableCell>
                                                    )
                                                )}
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {deliveries.length === 0 && !loading ? (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={5}
                                                        align="center"
                                                        sx={{
                                                            py: 6,
                                                            color: "#94a3b8",
                                                            fontStyle: "italic",
                                                        }}
                                                    >
                                                        No delivery notices found
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                deliveries.map((row) => {
                                                    const statusConfig = getStatusConfig(row.status);
                                                    return (
                                                        <TableRow
                                                            key={row.uniqueKey}
                                                            hover
                                                            sx={{
                                                                transition: "background-color 0.2s ease",
                                                                "&:hover": {
                                                                    backgroundColor: "rgba(148,163,184,0.08)",
                                                                },
                                                            }}
                                                        >
                                                            {/* COLUMN 1: Delivery ID */}
                                                            <TableCell sx={{ color: "#e5e7eb" }}>
                                                                <Typography fontWeight={500}>
                                                                    {row.deliveryId}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                                                                    {row.poName}
                                                                </Typography>
                                                            </TableCell>

                                                            {/* COLUMN 2: Status */}
                                                            <TableCell>
                                                                <Chip
                                                                    label={statusConfig.label}
                                                                    color={statusConfig.color as any}
                                                                    size="small"
                                                                    sx={{ fontWeight: 500, borderRadius: "6px" }}
                                                                />
                                                            </TableCell>

                                                            {/* COLUMN 3: Date */}
                                                            <TableCell sx={{ color: "#e5e7eb" }}>
                                                                {new Date(row.date).toLocaleDateString()}
                                                                <Typography variant="caption" display="block" sx={{ color: "#94a3b8" }}>
                                                                    {new Date(row.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                </Typography>
                                                            </TableCell>

                                                            {/* COLUMN 4: Item Number */}
                                                            <TableCell align="center">
                                                                <Chip
                                                                    label={row.itemCount}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    sx={{
                                                                        color: "#cbd5f5",
                                                                        borderColor: "rgba(203, 213, 245, 0.3)",
                                                                        minWidth: "30px"
                                                                    }}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    component="div"
                                    count={totalCount}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    sx={{
                                        color: "#cbd5f5",
                                        borderTop: "1px solid rgba(148,163,184,0.2)",
                                        ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                                            marginTop: "0",
                                            marginBottom: "0"
                                        },
                                        ".MuiTablePagination-selectIcon": {
                                            color: "#cbd5f5"
                                        }
                                    }}
                                />
                            </>
                        )}
                    </Paper>
                </Box>
            </IonContent>
        </IonPage>
    );
};

export default DeliveryNoticeDashboard;